import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCms } from '../context/CmsContext';
import BeforeAfterSlider from '../components/BeforeAfterSlider';

export default function BeforeAfterPage() {
  const { siteSettings } = useCms();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Før / Efter - Ecolaser ApS</title>
        <meta name="description" content="Oplev den vilde før/efter-effekt af vores kemifrie laserrensning på murværk, metal og graffiti. Se resultaterne med vores interaktive slider." />
        <meta property="og:title" content="Før / Efter - Ecolaser ApS" />
        <meta property="og:description" content="Oplev den vilde før/efter-effekt af vores kemifrie laserrensning på murværk, metal og graffiti. Se resultaterne med vores interaktive slider." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/for-og-efter-slangerup.png" />
        <html lang="da" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-44 pb-28 md:pt-52 md:pb-36 overflow-hidden bg-zinc-950">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/front-kbh-chr-d-9-high.webp?width=1800&quality=80&format=auto"
            alt="Ecolaser baggrund"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-left flex flex-col items-start gap-4">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight">Interaktiv Før / Efter</h1>
          <p className="text-zinc-200 text-sm md:text-base max-w-2xl leading-relaxed">
            Oplev den vilde effekt af vores kemifrie laserrensning på murværk, metal og graffiti. Træk i slideren for at sammenligne resultaterne direkte.
          </p>
        </div>
      </section>

      {/* Slider Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <BeforeAfterSlider columns={2} />
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="bg-white py-20 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl space-y-8 text-left">
            <h2 className="text-2xl md:text-4xl font-bold text-zinc-900">Hvorfor er forskellen så markant?</h2>
            <p className="text-zinc-600 leading-relaxed text-sm md:text-base">
              Laserrensning arbejder med ultrakorte, intense lyspulser. Når laserlyset rammer overfladen, absorberes energien øjeblikkeligt af snavset, graffitien eller rusten, som fordamper på et splitsekund (ablation). Selve underlaget reflekterer blot lyset og forbliver fuldstændig uberørt.
            </p>
            <p className="text-zinc-600 leading-relaxed text-sm md:text-base">
              Dette betyder, at vi kan fjerne selv de mest genstridige belægninger uden at slide på materialet, uden at beskadige fuger og uden at efterlade kemiske rester. Resultatet står knivskarpt og bringer materialets originale udtryk og patina flot tilbage.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-zinc-50 border-y border-zinc-200 py-24">
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col items-center gap-6">
          <h2 className="text-3xl md:text-5xl font-bold text-zinc-900 leading-tight">
            Vil du se, hvad laseren kan gøre for dig?
          </h2>
          <p className="text-zinc-600 text-sm md:text-base max-w-xl leading-relaxed">
            Vi dækker hele Danmark og tilbyder at lave en prøverensning på et mindre område, så du kan se resultatet, før du beslutter dig.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4 w-full sm:w-auto">
            <a
              href={siteSettings.phoneTel}
              className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-light text-white font-bold px-8 py-4 rounded-lg text-sm uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone" /> Ring direkte: {siteSettings.phone}
            </a>
            <Link
              to="/kontakt"
              className="w-full sm:w-auto bg-white hover:bg-zinc-100 text-zinc-800 font-bold px-8 py-4 rounded-lg text-sm uppercase tracking-wider border border-zinc-200 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-envelope" /> Udfyld formular
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
